import { toast } from 'react-toastify';
import { axiosInstance } from '../../repository/Repository';

import {
    BOOK_APPOINTMENT_ERROR,
    BOOK_APPOINTMENT_START,
    BOOK_APPOINTMENT_SUCCESS,
    GET_BUSINESS_APP_ERROR,
    GET_BUSINESS_APP_START,
    GET_BUSINESS_APP_SUCCESS,
    GET_APPOINTMENTS,
    PENDING_APPOINTMENTS,
    REJECTED_APPOINTMENTS,
    CANCEL_APPOINTMENT_LOADING,
    PREVIOUS_APP_PENDING,
    PREVIOUS_APP_SUCCESS,
    PREVIOUS_APP_ERROR,
    RESCHEDULE_APPOINTMENT_LOADING,
    BOOKING_STATS_PENDING,
    BOOKING_STATS_SUCCESS,
    BOOKING_STATS_ERROR,
    DASHBOARD_STATS_PENDING,
    DASHBOARD_STATS_SUCCESS,
    DASHBOARD_STATS_ERROR,
    EMAIL_STATS_PENDING,
    EMAIL_STATS_SUCCESS,
    EMAIL_STATS_ERROR,
    GET_AVAILABLE_SLOTS_START,
    GET_AVAILABLE_SLOTS_SUCCESS,
    GET_AVAILABLE_SLOTS_ERROR,
} from './types';
import moment from 'moment';
import { getAllBusinessAppointments } from '../calendar/actionCreator';

// =============BOOK APPOINTMENT=================
export const createAppointment =
    (data, onSuccess = () => {}, onError = () => {}) =>
    async (dispatch) => {
        dispatch({ type: BOOK_APPOINTMENT_START });
        try {
            const response = await axiosInstance.post(
                '/appointments/add',
                data
            );
            
            // Backend currently returns success: false with HTTP 201 on failure
            if (response?.data?.success === false) {
                throw new Error(response.data.error || 'Booking failed. Please try again.');
            }

            dispatch({ type: BOOK_APPOINTMENT_SUCCESS });
            toast.success('Appointment booked Successfully');
            onSuccess();
        } catch (error) {
            onError();
            toast.error(
                error?.data?.message || 
                error?.data?.error || 
                error?.message ||
                'There was an error! please try again later'
            );
            dispatch({ type: BOOK_APPOINTMENT_ERROR });
        }
    };

// =============GET ALL BUSINESS APPOINTMENT=================
export const getBusinessAppointments = () => async (dispatch) => {
    dispatch({ type: GET_BUSINESS_APP_START });
    try {
        const response = await axiosInstance.get('/appointments/business/upcoming');
        let data = response.data?.data[0];
        let currentDate = moment().format('YYYY-MM-DD');

        let todayAppointments = data.filter((item) =>
            moment(item.date).isSame(currentDate, 'day')
        );
        let upcomingAppointments = data.filter(
            (item) => !moment(item.date).isSame(currentDate, 'day')
        );

        dispatch({
            type: GET_BUSINESS_APP_SUCCESS,
            payload: { todayAppointments, upcomingAppointments },
        });
    } catch (error) {
        toast.error('There was an error! please try again later');
        dispatch({ type: GET_BUSINESS_APP_ERROR });
    }
};

// =============GET CUSTOMER APPOINTMENTS=================
export const getCustomerAppointments = () => async (dispatch) => {
    dispatch({ type: PENDING_APPOINTMENTS });
    try {
        const response = await axiosInstance.get('appointments/customer');
        dispatch({ type: GET_APPOINTMENTS, payload: response.data.data });
        // console.log('GetResponse: ', response.data.data);
        return response.data;
    } catch (error) {
        console.log('Error Fetching Business Details');
        dispatch({ type: REJECTED_APPOINTMENTS });
        console.log(error);
    }
};

export const getPreviousAppointments = () => async (dispatch) => {
    dispatch({ type: PREVIOUS_APP_PENDING });
    try {
        const response = await axiosInstance.get(
            'appointments/customer/previous'
        );
        dispatch({ type: PREVIOUS_APP_SUCCESS, payload: response.data.data });
        return response.data;
    } catch (error) {
        console.log('Error Fetching Business Details');
        dispatch({ type: PREVIOUS_APP_ERROR });
        console.log(error);
    }
};

export const setCancelAppointmentLoader = (val) => async (dispatch) => {
    dispatch({ type: CANCEL_APPOINTMENT_LOADING, payload: val });
};

export const cancelAppointment =
    (id, isRefunded, refundAmount, onSuccess = () => {}) =>
    async (dispatch) => {
        try {
            dispatch(setCancelAppointmentLoader(true));
            const response = await axiosInstance.put(`/appointments/customer/cancel/${id}`);
            dispatch(setCancelAppointmentLoader(false));
            dispatch(getCustomerAppointments());
            dispatch(getPreviousAppointments());
            
            const responseData = response?.data?.data?.[0];
            
            if (responseData?.refunded) {
                toast.success(responseData.message || `Appointment cancelled successfully. A refund of £${responseData.refundAmount} has been initiated.`);
            } else {
                toast.warning(responseData?.message || 'Appointment cancelled successfully. No refund is applicable under the cancellation policy.');
            }
            onSuccess();
        } catch (error) {
            toast.error(
                error?.data?.message ||
                    'Unable to cancel this appointment'
            );
            dispatch(setCancelAppointmentLoader(false));
        }
    };

// =============EDIT APPOINTMENT FROM BUSINESS SIDE=================
export const setRescheduleAppointmentLoader = (val) => async (dispatch) => {
    dispatch({ type: RESCHEDULE_APPOINTMENT_LOADING, payload: val });
};

export const rescheduleAppointment =
    (payload, onSuccess = () => {}) =>
    async (dispatch) => {
        try {
            dispatch(setRescheduleAppointmentLoader(true));
            await axiosInstance.put('/appointments/reschedule', payload);
            await dispatch(getAllBusinessAppointments());
            dispatch(setRescheduleAppointmentLoader(false));
            toast.success('Appointment edited successfully');
            onSuccess();
        } catch (error) {
            toast.error(
                error?.data?.message ||
                    'Unable to edit this appointment'
            );
            dispatch(setRescheduleAppointmentLoader(false));
        }
    };

// =============GET BOOKING STATS=================
export const getBookingStats = (period = 'today') => async (dispatch) => {
    dispatch({ type: BOOKING_STATS_PENDING });
    try {
        const response = await axiosInstance.get(
            `/appointments/business/completed-summary?period=${period}`
        );
        const data = response.data?.data?.[0] || {};
        dispatch({ 
            type: BOOKING_STATS_SUCCESS, 
            payload: {
                customerBooking: data.byClient?.totalAmount || 0,
                manualBooking: data.manual?.totalAmount || 0,
                aiAgentBooking: data.byAgent?.totalAmount || 0,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error Fetching Booking Stats:', error);
        dispatch({ type: BOOKING_STATS_ERROR });
    }
};

// =============GET DASHBOARD STATS=================
export const getDashboardStats = () => async (dispatch) => {
    dispatch({ type: DASHBOARD_STATS_PENDING });
    try {
        const response = await axiosInstance.get(
            '/appointments/business/dashboard-stats'
        );
        const data = response.data?.data?.[0] || {};
        dispatch({ 
            type: DASHBOARD_STATS_SUCCESS, 
            payload: {
                todayRevenue: data.todayRevenue || 0,
                yesterdayRevenue: data.yesterdayRevenue || 0,
                dayOverDayChangePercent: data.dayOverDayChangePercent || 0,
                trend: data.trend || 'profit',
                agentWeeklyBooking: data.agentWeeklyBooking || 0,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error Fetching Dashboard Stats:', error);
        dispatch({ type: DASHBOARD_STATS_ERROR });
    }
};

// =============GET EMAIL STATS=================
export const getEmailStats = () => async (dispatch) => {
    dispatch({ type: EMAIL_STATS_PENDING });
    try {
        const response = await axiosInstance.get(
            '/appointments/business/email-stats'
        );
        const data = response.data?.data?.[0] || {};
        dispatch({ 
            type: EMAIL_STATS_SUCCESS, 
            payload: {
                totalEmailsSent: data.totalEmailsSent || 0,
                bookingsCount: data.reminderBookings?.count || 0,
                revenueGenerated: data.reminderBookings?.revenue || 0,
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error Fetching Email Stats:', error);
        dispatch({ type: EMAIL_STATS_ERROR });
    }
};

// =============GET AVAILABLE TIME SLOTS=================
export const getAvailableSlots = (serviceId, date) => async (dispatch) => {
    dispatch({ type: GET_AVAILABLE_SLOTS_START });
    try {
        const response = await axiosInstance.get(
            `/appointments/availability?serviceId=${serviceId}&date=${date}`
        );
        
        const slotsData = response.data?.data?.[0]?.availableSlots ?? [];
        if (!slotsData || slotsData.length === 0) {
            toast.error('No available time slots found');
            dispatch({ type: GET_AVAILABLE_SLOTS_ERROR });
            return null;
        }
        
        if (slotsData && Array.isArray(slotsData)) {
            let formattedSlots = [];
            const isObjectFormat = slotsData.length > 0 && typeof slotsData[0] === 'object' && slotsData[0] !== null;

            if (isObjectFormat) {
                // Parse objects like { startTime: "10:00", endTime: "20:00" }
                formattedSlots = slotsData.map((slot) => {
                    if (slot && slot.startTime && slot.endTime) {
                        return `${slot.startTime}-${slot.endTime}`;
                    }
                    return null;
                }).filter(slot => slot !== null);
            } else {
                // Use string-based slots directly as-is
                formattedSlots = slotsData.filter(slot => typeof slot === 'string');
            }
            
            dispatch({ 
                type: GET_AVAILABLE_SLOTS_SUCCESS, 
                payload: formattedSlots 
            });
            return formattedSlots;
        } else {
            dispatch({ type: GET_AVAILABLE_SLOTS_ERROR });
            return null;
        }
    } catch (error) {
        console.error('Error fetching available slots:', error);
        toast.error('Error fetching available time slots');
        dispatch({ type: GET_AVAILABLE_SLOTS_ERROR });
        return null;
    }
};
